// Reading a partner's WhatsApp message (12 §12.7 channel 2, "Paste a message"): find the tracking
// numbers, barcodes, Prime piece labels and the partner's own references in it, find the status
// words (English and Tagalog), and propose one update per status for a person to confirm.
//
// Pure, forgiving and conservative: a unit that is not on the partner's open list, or that has no
// status next to it, is reported as unmatched instead of being guessed.
import type { PartnerStatus } from './box-types';

export type FailedReason = 'not_home' | 'wrong_address' | 'refused' | 'unreachable' | 'other';

export interface StatusReading {
  readonly status: PartnerStatus;
  /** Only for `failed_attempt`. */
  readonly reason?: FailedReason;
}

interface Phrase {
  readonly re: RegExp;
  readonly reading: StatusReading;
}

const L = '(?<![\\p{L}\\p{N}])';
const R = '(?![\\p{L}\\p{N}])';
const phrase = (source: string, reading: StatusReading): Phrase => ({
  re: new RegExp(`${L}(?:${source})${R}`, 'giu'),
  reading,
});

/**
 * Status words, most specific first. "Not delivered" must beat "delivered", and "wala" (Tagalog for
 * "none": nobody home) is a failed attempt, not a delivery.
 */
const PHRASES: readonly Phrase[] = [
  phrase(String.raw`(?:not|un)\s*delivered|hindi\s+na-?ihatid|di\s+na-?ihatid|failed\s+delivery`, {
    status: 'failed_attempt',
    reason: 'other',
  }),
  phrase(String.raw`refused|rejected|declined|ayaw(?:\s+tanggapin|\s+tumanggap)?|tumanggi`, {
    status: 'failed_attempt',
    reason: 'refused',
  }),
  phrase(
    String.raw`wrong\s+address|incorrect\s+address|mali(?:ng)?\s+(?:ang\s+)?address|hindi\s+mahanap|cannot\s+locate|can'?t\s+find`,
    { status: 'failed_attempt', reason: 'wrong_address' },
  ),
  phrase(
    String.raw`unreachable|no\s+answer|not\s+answering|cannot\s+be\s+reached|can'?t\s+contact|hindi\s+(?:ma-?contact|sumasagot)|di\s+(?:ma-?contact|sumasagot)`,
    { status: 'failed_attempt', reason: 'unreachable' },
  ),
  phrase(
    String.raw`not\s+home|no\s+one\s+(?:is\s+)?(?:home|there)|nobody\s+(?:is\s+)?(?:home|there)|wala\s+sa\s+bahay|walang\s+tao|wala(?:ng)?|closed`,
    { status: 'failed_attempt', reason: 'not_home' },
  ),
  phrase(String.raw`damaged|broken|basag|nasira|sira`, { status: 'damaged' }),
  phrase(String.raw`lost|missing|nawala`, { status: 'lost' }),
  phrase(
    String.raw`returned|returning|return\s+to\s+(?:hub|sender|bulacan|warehouse)|ibinalik|ibabalik|balik\s+sa`,
    { status: 'returned_to_hub' },
  ),
  phrase(
    String.raw`out\s+for\s+delivery|ofd|for\s+delivery|on\s+the\s+way|en\s*route|papunta|ihahatid|dadalhin|nasa\s+biyahe`,
    { status: 'out_for_delivery' },
  ),
  phrase(
    String.raw`delivered|naihatid|nai-?deliver|nadeliver|natanggap\s+na|received\s+by|tinanggap|completed|pod`,
    { status: 'delivered' },
  ),
  phrase(
    String.raw`received|picked\s+up|nareceive|natanggap|arrived\s+at\s+(?:our\s+)?(?:hub|warehouse)`,
    { status: 'received_by_partner' },
  ),
];

/**
 * The first status found in a short text: the partner's own words first (`statusMap`, lower case
 * keys), then the built-in English and Tagalog words. A cell of a spreadsheet, or one phrase.
 */
export function readStatusWords(
  text: string,
  statusMap: Readonly<Record<string, PartnerStatus>> = {},
): StatusReading | undefined {
  const lower = text.trim().toLowerCase();
  if (lower === '') return undefined;
  const builtIn = scanPhrases(text)[0]?.reading;
  const mapped =
    statusMap[lower] ??
    Object.entries(statusMap).find(([word]) => lower.includes(word.toLowerCase()))?.[1];
  if (mapped !== undefined) {
    if (mapped !== 'failed_attempt') return { status: mapped };
    return { status: mapped, reason: builtIn?.reason ?? 'other' };
  }
  return builtIn;
}

interface Hit {
  readonly index: number;
  readonly end: number;
  readonly reading: StatusReading;
}

function scanPhrases(text: string): Hit[] {
  const accepted: Hit[] = [];
  for (const p of PHRASES) {
    p.re.lastIndex = 0;
    for (const m of text.matchAll(p.re)) {
      const index = m.index;
      const end = index + m[0].length;
      if (accepted.some((a) => index < a.end && end > a.index)) continue;
      accepted.push({ index, end, reading: p.reading });
    }
  }
  return accepted.sort((a, b) => a.index - b.index);
}

// ---------------------------------------------------------------- identifiers

/** What the server knows about the partner's open units, so the parser never invents one. */
export interface UnitIndex {
  /** Every unit currently with the partner. */
  readonly units: ReadonlySet<string>;
  /** Tracking number to its units with the partner. */
  readonly byTracking: ReadonlyMap<string, readonly string[]>;
  /** The partner's own reference (see `normaliseRef`) to its units. */
  readonly byRef: ReadonlyMap<string, readonly string[]>;
}

/** Partner references are typed with dashes, spaces and any case: compare them without those. */
export function normaliseRef(ref: string): string {
  return ref.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export type MatchedBy = 'unit_id' | 'tracking_no' | 'partner_ref';

export interface Resolved {
  readonly unitIds: readonly string[];
  readonly via: MatchedBy;
}

/** One typed identifier to the units it means, or `undefined` when it names nothing open. */
export function resolveIdentifier(raw: string, index: UnitIndex): Resolved | undefined {
  const token = raw.trim().toUpperCase();
  if (index.units.has(token)) return { unitIds: [token], via: 'unit_id' };
  const tracked = index.byTracking.get(token);
  if (tracked && tracked.length > 0) return { unitIds: tracked, via: 'tracking_no' };
  const refd = index.byRef.get(normaliseRef(raw));
  if (refd && refd.length > 0) return { unitIds: refd, via: 'partner_ref' };
  return undefined;
}

interface Ident {
  readonly index: number;
  readonly end: number;
  readonly raw: string;
}

const LABEL_PATTERNS: readonly RegExp[] = [
  /(?<![A-Za-z0-9])EB\d{10}(?![A-Za-z0-9])/giu,
  /(?<![A-Za-z0-9])[A-Z]{3,4}\d{9}-P\d{1,2}(?![A-Za-z0-9])/giu,
  /(?<![A-Za-z0-9])[A-Z]{3,4}\d{9}(?![A-Za-z0-9-])/giu,
];
const WORD = /[A-Za-z0-9][A-Za-z0-9\-_/]*[A-Za-z0-9]/gu;

function findIdentifiers(text: string, index: UnitIndex): Ident[] {
  const found: Ident[] = [];
  const free = (a: number, b: number) => !found.some((f) => a < f.end && b > f.index);
  // The three label shapes cannot overlap one another, so they need no check.
  for (const re of LABEL_PATTERNS) {
    for (const m of text.matchAll(re)) {
      found.push({ index: m.index, end: m.index + m[0].length, raw: m[0] });
    }
  }
  // The partner's own references, and the units and tracking numbers of other shapes (series and
  // numbers typed by hand, 03 §3.5): only words that are known, so ordinary words never match.
  for (const m of text.matchAll(WORD)) {
    const word = m[0].toUpperCase();
    const known =
      index.byRef.has(normaliseRef(m[0])) || index.units.has(word) || index.byTracking.has(word);
    if (!known) continue;
    if (free(m.index, m.index + m[0].length)) {
      found.push({ index: m.index, end: m.index + m[0].length, raw: m[0] });
    }
  }
  return found.sort((a, b) => a.index - b.index);
}

// -------------------------------------------------------------------- reading

export interface ParsedProposal {
  readonly status: PartnerStatus;
  readonly reason: FailedReason | null;
  readonly receiverName: string;
  readonly collectedMinor: number | null;
  readonly unitIds: string[];
  /** The text the proposal came from, for the audit row. */
  readonly original: string;
  readonly matchedBy: MatchedBy[];
  readonly warnings: string[];
}

export interface Unmatched {
  readonly text: string;
  readonly why: string;
}

export interface ParseResult {
  readonly proposals: ParsedProposal[];
  readonly unmatched: Unmatched[];
}

const NAME_WORD = String.raw`[\p{L}][\p{L}.'-]*(?=$|[\s,;:])`;
const RECEIVER = new RegExp(
  String.raw`(?:received\s+by|tinanggap\s+ni|nakuha\s+ni|signed\s+by|recipient|receiver)\s*[:-]?\s*(${NAME_WORD}(?:\s+${NAME_WORD}){0,3})`,
  'iu',
);
const COLLECTED =
  /(?:collected|cod|nakolekta|nakuha\s+na\s+bayad|bayad)\s*[:-]?\s*(?:php|₱|p)?\s*(\d[\d,]*(?:\.\d{1,2})?)/iu;

function receiverOf(segment: string): string {
  const name = RECEIVER.exec(segment)?.[1]?.trim() ?? '';
  // Trailing status words are not part of a name ("received by Juan delivered").
  return name.replace(/\s+(?:delivered|naihatid|na)$/i, '').slice(0, 80);
}

function collectedOf(segment: string, exponent: number): number | null {
  const amount = COLLECTED.exec(segment)?.[1];
  if (amount === undefined) return null;
  const value = Number(amount.replace(/,/g, ''));
  return Number.isFinite(value) && value > 0 ? Math.round(value * 10 ** exponent) : null;
}

/** Splits at line ends, semicolons and sentence ends, keeping each piece's offset in the text. */
function segmentsOf(text: string): { start: number; end: number }[] {
  const out: { start: number; end: number }[] = [];
  let start = 0;
  const cut = /[\n;]|\.(?=\s+\p{Lu}|\s*$)/gu;
  for (const m of text.matchAll(cut)) {
    out.push({ start, end: m.index });
    start = m.index + 1;
  }
  out.push({ start, end: text.length });
  return out.filter((s) => text.slice(s.start, s.end).trim() !== '');
}

export interface Assigned {
  readonly unitId: string;
  readonly reading: StatusReading;
  readonly receiver: string;
  readonly collected: number | null;
  readonly via: MatchedBy;
  readonly segment: string;
}

/**
 * Reads a pasted message into proposed updates.
 *
 * A status word before the identifiers covers everything after it until the next status word
 * ("Delivered: EB1, EB2; not home: EB3"). A status word right after identifiers covers just those
 * ("EB1 delivered, EB2 not home"). Identifiers with no status are reported, not guessed.
 */
export function parsePartnerMessage(
  text: string,
  index: UnitIndex,
  options: { currencyExponent?: number } = {},
): ParseResult {
  const exponent = options.currencyExponent ?? 2;
  const assigned: Assigned[] = [];
  const unmatched: Unmatched[] = [];
  let header: StatusReading | undefined;
  let sawIdentifier = false;
  let sawStatus = false;

  for (const seg of segmentsOf(text)) {
    const segment = text.slice(seg.start, seg.end);
    const idents = findIdentifiers(segment, index);
    const hits = scanPhrases(segment);
    const receiver = receiverOf(segment);
    const collected = collectedOf(segment, exponent);
    if (hits.length > 0) sawStatus = true;
    const events = [
      ...idents.map((i) => ({ at: i.index, ident: i })),
      ...hits.map((h) => ({ at: h.index, hit: h })),
    ].sort((a, b) => a.at - b.at);

    let pending: { ident: Ident; resolved: Resolved }[] = [];
    const give = (resolved: Resolved, reading: StatusReading) => {
      for (const unitId of resolved.unitIds) {
        assigned.push({
          unitId,
          reading,
          receiver,
          collected,
          via: resolved.via,
          segment: segment.trim(),
        });
      }
    };
    for (const ev of events) {
      if ('ident' in ev) {
        sawIdentifier = true;
        const resolved = resolveIdentifier(ev.ident.raw, index);
        if (!resolved) {
          unmatched.push({
            text: ev.ident.raw.toUpperCase(),
            why: 'No unit with this partner has that number or reference.',
          });
        } else if (header !== undefined) {
          give(resolved, header);
        } else {
          pending.push({ ident: ev.ident, resolved });
        }
      } else if (pending.length > 0) {
        // The word follows the identifiers it describes.
        for (const p of pending) give(p.resolved, ev.hit.reading);
        pending = [];
        header = undefined;
      } else {
        header = ev.hit.reading;
      }
    }
    for (const p of pending) {
      unmatched.push({
        text: p.ident.raw.toUpperCase(),
        why: 'No status word (delivered, not home, out for delivery …) next to it.',
      });
    }
  }

  if (!sawIdentifier && unmatched.length === 0) {
    unmatched.push({
      text: text.trim().slice(0, 120),
      why: sawStatus
        ? 'No tracking number, barcode or partner reference found.'
        : 'No tracking number, barcode, partner reference or status word found.',
    });
  }

  return { proposals: groupProposals(assigned), unmatched };
}

/** One proposal per status, reason, receiver and collected amount; a later statement about a unit wins. */
export function groupProposals(assigned: readonly Assigned[]): ParsedProposal[] {
  const last = new Map<string, Assigned>();
  const overridden = new Set<string>();
  for (const a of assigned) {
    if (last.has(a.unitId)) overridden.add(a.unitId);
    last.set(a.unitId, a);
  }
  const groups = new Map<string, { sample: Assigned; items: Assigned[] }>();
  for (const a of last.values()) {
    const key = [a.reading.status, a.reading.reason ?? '', a.receiver, a.collected ?? ''].join('|');
    const g = groups.get(key) ?? { sample: a, items: [] };
    g.items.push(a);
    groups.set(key, g);
  }
  return [...groups.values()].map(({ sample, items }) => {
    const warnings: string[] = [];
    const again = items.filter((i) => overridden.has(i.unitId)).map((i) => i.unitId);
    if (again.length > 0) {
      warnings.push(`${again.join(', ')} appeared more than once; the last status was used.`);
    }
    return {
      status: sample.reading.status,
      reason: sample.reading.reason ?? null,
      receiverName: sample.receiver,
      collectedMinor: sample.collected,
      unitIds: items.map((i) => i.unitId),
      original: [...new Set(items.map((i) => i.segment))].join('\n').slice(0, 4000),
      matchedBy: [...new Set(items.map((i) => i.via))],
      warnings,
    };
  });
}
